// src/utils/backgroundPositionResolver.ts
// PURE background decision resolver — no DOM or GSAP imports here.
// Returns a structured decision describing how the background should animate.

// ---------- Types ----------
export type XY = { x: string; y: string };

export type SlideDecision = {
  kind: 'slide';
  from: XY;
  to: XY;
  duration: string | number; // e.g., "12000ms" or 12000
  ease?: string;             // e.g., "power1.inOut"
  seed: { position: XY };
  vars: Record<string, string>;
};

export type KenBurnsDecision = {
  kind: 'kenburns';
  mode: 'in' | 'out';         // grow or shrink
  origin: string;             // e.g., "50% 35%"
  from: XY & { scale: string | number };
  to:   XY & { scale: string | number };
  duration: string | number;  // e.g., "20000ms"
  seed: { position: XY; scale: string | number };
  vars: Record<string, string>; // e.g., { "--kb-origin": "50% 35%" }
};

export type StaticDecision = {
  kind: 'static';
  position: string;           // e.g., "50% 50%"
  seed: { position: XY };
  vars: Record<string, string>;
};

export type BgDecision = SlideDecision | KenBurnsDecision | StaticDecision;

// ---------- Local helpers (kept here to avoid circular deps) ----------
const ANCHOR_TO_PERCENT: Record<string, string> = {
  top: '50% 0%',
  'middle-top': '50% 35%',
  middle: '50% 50%',
  center: '50% 50%',
  'middle-bottom': '50% 65%',
  bottom: '50% 100%',
  left: '0% 50%',
  'left-center': '35% 50%',
  'center-right': '65% 50%',
  right: '100% 50%',
  'center-left': '35% 50%',
  'top-center': '50% 0%',
  'bottom-center': '50% 100%',
};

function normalizePos(input?: string, fallback = '50% 50%'): XY {
  const toPct = (v: string) =>
    v === 'left' ? '0%' :
    v === 'center' || v === 'middle' ? '50%' :
    v === 'right' ? '100%' :
    v === 'top' ? '0%' :
    v === 'bottom' ? '100%' : v;

  if (!input) {
    const [fx, fy] = fallback.split(' ');
    return { x: fx || '50%', y: fy || '50%' };
  }

  const t = input.trim().toLowerCase();
  // Named anchors
  if (ANCHOR_TO_PERCENT[t]) {
    const [x, y] = ANCHOR_TO_PERCENT[t].split(' ');
    return { x, y };
  }

  // Split into x / y but keep calc(...) intact
  // Pattern: first token = non-space; second = the rest (may contain spaces)
  const m = t.match(/^(\S+)\s+(.+)$/);
  if (m) {
    return { x: toPct(m[1]), y: toPct(m[2]) };
  }

  // Single token fallback
  if (/%$/.test(t)) return { x: t, y: '50%' };
  return { x: '50%', y: '50%' };
}

function msNorm(v: string | number | undefined, dflt = '12000ms'): string {
  if (v == null) return dflt;
  const t = String(v).trim().toLowerCase();
  if (/^\d+ms$/.test(t)) return t;
  if (/^\d+s$/.test(t)) return `${parseFloat(t) * 1000}ms`;
  if (/^\d+$/.test(t)) return `${t}ms`;
  return dflt;
}

function easeNorm(v?: string, dflt = 'power1.inOut'): string {
  const t = (v || '').trim().toLowerCase();
  if (!t) return dflt;
  if (t === 'ease-in') return 'power1.in';
  if (t === 'ease-out') return 'power1.out';
  if (t === 'ease-in-out' || t === 'easeinout') return 'power1.inOut';
  return v!;
}

function amountToPercent(a?: string): string {
  const t = String(a ?? '25%').trim().toLowerCase();
  if (t === 'short') return '12%';
  if (t === 'medium') return '25%';
  if (t === 'long') return '40%';
  return /%$/.test(t) ? t : `${t}%`;
}

function parsePanShorthand(s?: string): { from: XY; to: XY } | null {
  if (!s) return null;
  const m = s.match(/^(.+?)->(.+)$/);
  if (!m) return null;
  return { from: normalizePos(m[1].trim()), to: normalizePos(m[2].trim()) };
}

function chooseDefaultMode(assetIsVertical: boolean, viewportIsVertical: boolean): 'kenburns' | 'pan' {
  return assetIsVertical === viewportIsVertical ? 'kenburns' : 'pan';
}

// ---------- Resolver ----------
export function resolveBackgroundPosition(
  attrs: Record<string, any>,
  screenIsVertical: boolean,
  source: 'background-v' | 'background-h' | null
): BgDecision {
  // Basic inputs
  const animTokenRaw = String(attrs['background-animation'] ?? attrs['background-anim'] ?? '').toLowerCase().trim();
  const animToken = animTokenRaw;
  const posToken = String(attrs['background-position'] ?? attrs['background_pos'] ?? '');

  // NEW: per-orientation overrides (optional)
  const posTokenH = String(attrs['background-position-h'] ?? '').trim();
  const posTokenV = String(attrs['background-position-v'] ?? '').trim();

  // Choose authored position by current viewport orientation with sensible fallbacks
  const samePosRaw = screenIsVertical
    ? (posTokenV || posToken || '50% 50%')
    : (posTokenH || posToken || '50% 50%');

  const oppPosRaw = screenIsVertical
    ? (posTokenV || posToken || '50% 50%')
    : (posTokenH || posToken || '50% 50%');

  // Use authored positions on both orientations (per-orientation pins win when provided)
  const anchorOpp = normalizePos(oppPosRaw || '50% 50%');
  const centerXY: XY = { x: '50%', y: '50%' };

  // Detect explicit PAN info (authored only via start/stop or shorthand; no anim token for pan)
  const hasPanExplicit = !!attrs['background-pan-start'] && !!attrs['background-pan-stop'];
  const panSh = parsePanShorthand(String(attrs['background-pan'] ?? '').trim());
  const hasPan = hasPanExplicit || !!panSh;

  // Detect explicit KEN BURNS info (animation token is only used for KB types)
  const kbNamed = animToken.startsWith('kenburns') || animToken.startsWith('kb');
  const hasKenBurns = kbNamed;

  const assetIsVertical = source === 'background-v';
  const isOpposite = assetIsVertical !== screenIsVertical;

  // ---------------- Opposite orientation logic ----------------
  if (isOpposite) {
    // 1) PAN has priority on opposite if authored
    if (hasPan) {
      const fromXY = hasPanExplicit
        ? normalizePos(String(attrs['background-pan-start']).trim())
        : panSh?.from || anchorOpp;
      const toXY = hasPanExplicit
        ? normalizePos(String(attrs['background-pan-stop']).trim())
        : panSh?.to || anchorOpp;

      const duration = msNorm(attrs['background-pan-duration'] ?? attrs['background-slide-duration'] ?? '12000ms');
      const ease = easeNorm(attrs['background-pan-ease'] ?? attrs['background-slide-ease'] ?? 'power1.inOut');

      return {
        kind: 'slide',
        from: fromXY,
        to: toXY,
        duration,
        ease,
        seed: { position: { x: fromXY.x, y: fromXY.y } },
        vars: {
          '--slide-from-x': fromXY.x,
          '--slide-from-y': fromXY.y,
          '--slide-to-x': toXY.x,
          '--slide-to-y': toXY.y,
          '--background-slide-duration': duration,
          '--background-slide-ease': ease,
          '--pan-duration': duration,
          '--pan-ease': ease,
        },
      };
    }

    // 2) If ONLY Ken Burns is authored (no pan), allow KB even on opposite
    if (hasKenBurns && !hasPan) {
      const mode: 'in' | 'out' = /out$/.test(animToken) ? 'out' : 'in';
      const originStr = String(attrs['background-origin'] ?? attrs['kb-origin'] ?? `${anchorOpp.x} ${anchorOpp.y}`);
      const origin = originStr.includes(' ') ? originStr : `${originStr} 50%`;
      const fromScale = String(attrs['kb-from-scale'] ?? (mode === 'in' ? '1.0' : '1.12'));
      const toScale = String(attrs['kb-to-scale'] ?? (mode === 'in' ? '1.12' : '1.0'));
      const duration = msNorm(attrs['background-animation-duration'] ?? attrs['kb-duration'] ?? '20000ms');

      return {
        kind: 'kenburns',
        mode,
        origin,
        from: { x: anchorOpp.x, y: anchorOpp.y, scale: fromScale },
        to:   { x: anchorOpp.x, y: anchorOpp.y, scale: toScale },
        duration,
        seed: { position: { x: anchorOpp.x, y: anchorOpp.y }, scale: fromScale },
        vars: { '--kb-origin': origin },
      };
    }

    // 3) No animations authored → static using background-position (if any) on opposite
    // Honor per-orientation override (or global) for opposite orientation
    const anchor = anchorOpp;
    return {
      kind: 'static',
      position: `${anchor.x} ${anchor.y}`,
      seed: { position: { x: anchor.x, y: anchor.y } },
      vars: {},
    };
  }

  // ---------------- Same orientation logic ----------------
  // PAN is never used on the same orientation; ignore it if authored.

  // Ken Burns only if explicitly authored
  if (hasKenBurns) {
    const anchorSame = normalizePos(samePosRaw || '50% 50%'); // honor pins even for KB seed
    const mode: 'in' | 'out' = /out$/.test(animToken) ? 'out' : 'in';
    const originStr = String(attrs['background-origin'] ?? attrs['kb-origin'] ?? `${anchorSame.x} ${anchorSame.y}`);
    const origin = originStr.includes(' ') ? originStr : `${originStr} 50%`;
    const fromScale = String(attrs['kb-from-scale'] ?? (mode === 'in' ? '1.0' : '1.12'));
    const toScale = String(attrs['kb-to-scale'] ?? (mode === 'in' ? '1.12' : '1.0'));
    const duration = msNorm(attrs['background-animation-duration'] ?? attrs['kb-duration'] ?? '20000ms');

    return {
      kind: 'kenburns',
      mode,
      origin,
      from: { x: anchorSame.x, y: anchorSame.y, scale: fromScale },
      to:   { x: anchorSame.x, y: anchorSame.y, scale: toScale },
      duration,
      seed: { position: { x: anchorSame.x, y: anchorSame.y }, scale: fromScale },
      vars: { '--kb-origin': origin },
    };
  }

  // Otherwise, STATIC on same orientation honoring per-orientation pins
  const anchorSame = normalizePos(samePosRaw || '50% 50%');
  return {
    kind: 'static',
    position: `${anchorSame.x} ${anchorSame.y}`,
    seed: { position: { x: anchorSame.x, y: anchorSame.y } },
    vars: {},
  };
}